import { z } from "@hono/zod-openapi";
import {
  type TmsEventKindVersion,
  type tmsEventBase,
  UNCATEGORIZED_CATEGORY,
  type UncategorizedCategory,
} from "./base";
import { tmsEventCatalog } from "./catalog";

// Everything in this file is *derived* from `catalog.ts` and nothing is hand-listed: no `z.enum` over a written-out
// list, no kind → category table typed by hand, no schema built anywhere but `tmsEventKind`. That is what makes the
// map the single declaration — adding a kind is a file and a placement, and the enums, the lookups, and both unions
// follow on their own.

type Catalog = typeof tmsEventCatalog;

/**
 * The categories: the map's top-level keys, minus the reserved one.
 */
export type TmsEventCategory = Exclude<keyof Catalog & string, UncategorizedCategory>;

/**
 * The kinds one category holds.
 */
export type TmsEventKindOf<TCategory extends keyof Catalog> = keyof Catalog[TCategory] & string;

/**
 * Every kind in the vocabulary, read off the map's second level.
 */
export type TmsEventKind = { [TCategory in keyof Catalog]: TmsEventKindOf<TCategory> }[keyof Catalog];

/**
 * The category a kind sits under — including the reserved key, which {@link tmsEventKindCategory} reports as `null`.
 */
export type TmsEventCategoryOf<TKind extends TmsEventKind> = {
  [TCategory in keyof Catalog]: TKind extends TmsEventKindOf<TCategory> ? TCategory : never;
}[keyof Catalog];

/**
 * Collapses the map's per-category records into one record keyed by kind.
 *
 * Sound only because kind names are unique across categories, which {@link indexVersionsByKind} asserts at module
 * load — the flat enum and the wire discriminator both need that uniqueness, so it is checked rather than assumed.
 */
type UnionToIntersection<TUnion> = (TUnion extends unknown ? (union: TUnion) => void : never) extends (
  intersection: infer TIntersection,
) => void
  ? TIntersection
  : never;

type TmsEventVersionsByKind = UnionToIntersection<{ [TCategory in keyof Catalog]: Catalog[TCategory] }[keyof Catalog]>;

/**
 * Every version a kind declares, keyed by version number.
 */
export type TmsEventVersionsOf<TKind extends TmsEventKind> = TmsEventVersionsByKind[TKind &
  keyof TmsEventVersionsByKind];

/**
 * The version numbers a kind may be declared at.
 */
export type TmsEventVersionOf<TKind extends TmsEventKind> = keyof TmsEventVersionsOf<TKind> & number;

/**
 * A kind-version's wire DTO schema, and its `facts` schema.
 *
 * Inferred out rather than indexed: for a generic kind TypeScript cannot prove the map's leaf carries either
 * property, and constraining the leaf type to prove it is what widens the version keys and loses the bound that
 * refuses an undeclared version. Both resolve concretely wherever the kind is a literal, which is every call site.
 */
type SchemaOf<TKindVersion> = TKindVersion extends { schema: infer TSchema } ? TSchema : never;

type FactsSchemaOf<TKindVersion> = TKindVersion extends { facts: infer TFacts } ? TFacts : never;

/**
 * Any catalog's shape, structurally.
 *
 * The derivations below read a catalog rather than closing over the real one, so a fixture built with
 * `tmsEventCatalogOf` exercises the same code — no test needs to replace this module's imports to reach an edge
 * the launch catalog happens not to have.
 */
type AnyTmsEventCatalog = Readonly<
  Record<string, Readonly<Record<string, Readonly<Record<number, TmsEventKindVersion>>>>>
>;

/**
 * Indexes every kind-version by kind, refusing a kind name that two categories both claim.
 *
 * Uniqueness is what lets the flat kind enum exist and what lets `kind` discriminate the wire union; without it a
 * lookup by kind would be ambiguous and the union would have two arms answering to one tag. Throwing here means the
 * module fails to load rather than a stream serving one of two schemas arbitrarily.
 */
function indexVersionsByKind(catalog: AnyTmsEventCatalog): Record<string, Record<number, TmsEventKindVersion>> {
  const byKind: Record<string, Record<number, TmsEventKindVersion>> = {};

  for (const [category, kinds] of Object.entries(catalog)) {
    for (const [kind, versions] of Object.entries(kinds as Record<string, Record<number, TmsEventKindVersion>>)) {
      const claimed = byKind[kind];
      if (claimed !== undefined) {
        const first = Object.values(claimed)[0]?.id.category;
        throw new Error(`TMS event kind "${kind}" is declared under both "${first}" and "${category}"`);
      }
      byKind[kind] = versions;
    }
  }

  return byKind;
}

const versionsByKind = indexVersionsByKind(tmsEventCatalog);

const categoryKeys = Object.keys(tmsEventCatalog) as (keyof Catalog & string)[];

export const TMS_EVENT_CATEGORIES: readonly TmsEventCategory[] = categoryKeys.filter(
  (category): category is TmsEventCategory => category !== UNCATEGORIZED_CATEGORY,
);

export const TMS_EVENT_KINDS: readonly TmsEventKind[] = Object.keys(versionsByKind) as TmsEventKind[];

// Neither enum carries a wire name: the spec registers their published narrowings from `published.ts` under the names
// these used to hold, so an internal kind or category cannot reach a generated client through the vocabulary either.
export const TmsEventCategorySchema = z.enum(TMS_EVENT_CATEGORIES);

export const TmsEventKindSchema = z.enum(TMS_EVENT_KINDS);

/**
 * The category each kind sits under, typed per kind and `null` for the reserved key.
 *
 * `tmsEventKindCategory.fuelStop` is the literal `"fuel"`, not `TmsEventCategory` — so a display table keyed by kind
 * cannot quietly disagree with the map, and a miscategorization is a type error at the use site.
 */
export const tmsEventKindCategory = Object.fromEntries(
  Object.entries(tmsEventCatalog).flatMap(([category, kinds]) =>
    Object.keys(kinds).map((kind) => [kind, category === UNCATEGORIZED_CATEGORY ? null : category]),
  ),
) as {
  readonly [TKind in TmsEventKind]: TmsEventCategoryOf<TKind> extends UncategorizedCategory
    ? null
    : TmsEventCategoryOf<TKind>;
};

/**
 * The kind-version one triple names.
 *
 * `version` is required, and deliberately so: there is no notion of a current version anywhere in this package, so
 * nothing silently moves to a newer schema when one ships. A version the kind does not declare is a type error rather
 * than a runtime miss, the parameter being bounded by that kind's own version keys — `{ kind: "fuelStop", version: 2 }`
 * does not compile while only version 1 exists. Adding a version is therefore a compile error at every site that has
 * to be reconsidered, which is where the decision to serve it belongs.
 *
 * @param id - `kind` indexes the map and `version` selects among that kind's own declared versions.
 * @throws if the kind is absent from the catalog, which the type system already refuses and only a cast can reach.
 */
export function tmsEventKindVersionFor<TKind extends TmsEventKind, TVersion extends TmsEventVersionOf<TKind>>(id: {
  kind: TKind;
  version: TVersion;
}): TmsEventVersionsOf<TKind>[TVersion] {
  const versions = versionsByKind[id.kind];
  if (versions === undefined) {
    throw new Error(`Unknown TMS event kind "${id.kind}"`);
  }

  const kindVersion = versions[id.version];
  if (kindVersion === undefined) {
    throw new Error(`TMS event kind "${id.kind}" has no version ${id.version}`);
  }

  return kindVersion as TmsEventVersionsOf<TKind>[TVersion];
}

/**
 * The versions one kind declares, ascending.
 *
 * The enumeration that mandatory versions imply: with no current-version lookup, a caller holding a runtime kind has
 * no other way to learn what it may ask for — which is what validating a client's requested version needs, and what
 * lets a check that must cover every version iterate rather than assume one.
 *
 * @param kind - A catalog kind; the type system refuses any other, so a throw here means a cast reached it.
 * @throws if the kind is absent from the catalog.
 */
export function tmsEventVersionsFor(kind: TmsEventKind): readonly number[] {
  const versions = versionsByKind[kind];
  if (versions === undefined) {
    throw new Error(`Unknown TMS event kind "${kind}"`);
  }

  return Object.keys(versions)
    .map(Number)
    .sort((left, right) => left - right);
}

/**
 * The wire DTO schema for one kind at one of its declared versions.
 */
export function tmsEventSchemaFor<TKind extends TmsEventKind, TVersion extends TmsEventVersionOf<TKind>>(id: {
  kind: TKind;
  version: TVersion;
}): SchemaOf<TmsEventVersionsOf<TKind>[TVersion]> {
  const kindVersion = tmsEventKindVersionFor(id) as TmsEventKindVersion;
  return kindVersion.schema as SchemaOf<TmsEventVersionsOf<TKind>[TVersion]>;
}

/**
 * The `facts` schema for one kind at one of its declared versions.
 */
export function tmsEventFactsSchemaFor<TKind extends TmsEventKind, TVersion extends TmsEventVersionOf<TKind>>(id: {
  kind: TKind;
  version: TVersion;
}): FactsSchemaOf<TmsEventVersionsOf<TKind>[TVersion]> {
  const kindVersion = tmsEventKindVersionFor(id) as TmsEventKindVersion;
  return kindVersion.facts as FactsSchemaOf<TmsEventVersionsOf<TKind>[TVersion]>;
}

/**
 * Every version a kind declares, as one type.
 */
type DeclaredKindVersionOf<TKind extends TmsEventKind> = TmsEventVersionsOf<TKind>[TmsEventVersionOf<TKind>];

type KindArmSchema = { [TKind in TmsEventKind]: SchemaOf<DeclaredKindVersionOf<TKind>> }[TmsEventKind];
type KindArmFactsSchema = { [TKind in TmsEventKind]: FactsSchemaOf<DeclaredKindVersionOf<TKind>> }[TmsEventKind];

/**
 * One arm per kind, carrying every version that kind declares.
 *
 * A kind with a single version contributes that schema; a kind with several contributes a union discriminated on
 * `version`, nested inside the outer one. The nesting is not presentational — flattening every kind-version into one
 * `kind` union would not fail, because zod accepts a repeated discriminator value and keeps only the first arm, so a
 * document written under version 2 would be parsed against version 1's schema and rejected for fields it correctly
 * carries. Discriminating per kind first keeps `kind` a sound tag no matter how many versions sit under it.
 *
 * The version union's OpenAPI discriminator is declared bare: the emitter maps only string discriminator values, so
 * numeric versions would otherwise leave an empty `mapping` that swagger-typescript-api renders as an empty union arm.
 *
 * @param index - The kind-versions to build arms over, as {@link indexVersionsByKind} returns them.
 * @param select - Takes the wire DTO schema or the facts schema off a kind-version; the two unions differ only here.
 * @throws if the catalog declares no kinds, or a kind with no versions — neither of which the map can express.
 */
function armsPerKind(
  index: Record<string, Record<number, TmsEventKindVersion>>,
  select: (kindVersion: TmsEventKindVersion) => z.ZodType,
): [z.ZodType, ...z.ZodType[]] {
  const arms = Object.keys(index).map((kind) => {
    const [first, ...rest] = Object.values(index[kind] ?? {}).map(select);
    if (first === undefined) {
      throw new Error(`TMS event kind "${kind}" declares no version`);
    }

    return rest.length === 0
      ? first
      : z
          .discriminatedUnion("version", [first, ...rest] as never)
          .openapi({ discriminator: { propertyName: "version" } });
  });

  const [firstArm, ...restArms] = arms;
  if (firstArm === undefined) {
    throw new Error("The TMS event catalog declares no kinds");
  }

  return [firstArm, ...restArms];
}

// One cast each, at the seam between the runtime walk above and the precise union the type system needs. The walk is
// over the map's own kind list and its own version keys, so the arms are exactly the catalog's by construction.
const kindArms = armsPerKind(versionsByKind, (kindVersion) => kindVersion.schema) as unknown as [
  KindArmSchema,
  ...KindArmSchema[],
];
const kindFactsArms = armsPerKind(versionsByKind, (kindVersion) => kindVersion.facts) as unknown as [
  KindArmFactsSchema,
  ...KindArmFactsSchema[],
];

/**
 * The wire union over any catalog, built exactly as {@link TmsEventSchema} is built over the real one.
 *
 * Exported because the launch catalog cannot reach two of this module's cases: it declares no kind name twice,
 * and no kind at two versions. Both are reachable from a fixture catalog, and running the real derivation over
 * one is a truer test than replacing this module's catalog import with a stub.
 *
 * @param catalog - Any catalog, real or fixture; the real one is what `TmsEventSchema` is built over.
 * @throws if the catalog names one kind under two categories.
 */
export function tmsEventSchemaOver(catalog: AnyTmsEventCatalog): z.ZodType {
  return z.discriminatedUnion(
    "kind",
    armsPerKind(indexVersionsByKind(catalog), (kindVersion) => kindVersion.schema) as never,
  );
}

/**
 * Every kind at every version it declares, discriminated on `kind`.
 *
 * The full union, deliberately: a client names the version it wants and gets exactly that schema, and a version stays
 * in the union until it is explicitly deprecated as unused rather than dropping out when a newer one lands. No stream
 * on it either — streams are homogeneous collections the registry decides, so a document needs no discriminator.
 *
 * Not the wire schema. That is `PublishedTmsEventSchema` in `published.ts`, this union narrowed to the kinds whose
 * stream leaves the backend; this one carries no OpenAPI name so an internal kind can never reach the spec through it.
 */
export const TmsEventSchema = z.discriminatedUnion("kind", kindArms);

export type TmsEvent = z.infer<typeof TmsEventSchema>;

/**
 * One kind's DTO, narrowed out of the union.
 */
export type TmsEventOf<TKind extends TmsEventKind> = Extract<TmsEvent, { kind: TKind }>;

/**
 * Every kind's facts at every declared version: what any stored `facts` jsonb parses as, whenever it was written.
 */
export const TmsEventFactsSchema = z.discriminatedUnion("kind", kindFactsArms);

export type TmsEventFacts = z.infer<typeof TmsEventFactsSchema>;

/**
 * One kind's facts, narrowed out of the union.
 */
export type TmsEventFactsOf<TKind extends TmsEventKind> = Extract<TmsEventFacts, { kind: TKind }>;

/**
 * What a subject's normalizer produces: references and facts, the DTO minus the base the writer supplies.
 *
 * Distributed over the kind union deliberately. A bare `Omit` across a union keeps only the keys every arm shares,
 * which would erase exactly the per-kind facts this type exists to carry.
 */
export type TmsEventBodyOf<TKind extends TmsEventKind> =
  TmsEventOf<TKind> extends infer TEvent
    ? TEvent extends unknown
      ? Omit<TEvent, keyof z.infer<typeof tmsEventBase>>
      : never
    : never;
